/**
 * React Query hooks for self-service billing (PA-7).
 *
 * - Billing data (company info): GET + POST
 * - Orders list: paginated GET
 * - Order detail: GET by ID
 *
 * staleTime: 2 minutes (PA-7 spec requirement)
 *
 * @see docs/specifications/2026-03-12-pa7-user-self-service-billing-ui.md
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  selfServiceBillingApi,
  type SelfServiceBillingData,
  type SaveBillingDataPayload,
  type SelfServiceOrderListResponse,
  type SelfServiceOrderDetail,
} from '../services/selfServiceBillingApi';

/** 2 minutes staleTime per PA-7 spec */
const STALE_TIME = 2 * 60 * 1000;

// =============================================================================
// QUERY KEYS
// =============================================================================

export const SELF_SERVICE_BILLING_KEYS = {
  billingData: ['self-service', 'billing-data'] as const,
  orders: (params: { limit: number; offset: number }) =>
    ['self-service', 'orders', params] as const,
  orderDetail: (id: string) => ['self-service', 'order', id] as const,
};

// =============================================================================
// BILLING DATA (company info)
// =============================================================================

/**
 * Fetch current billing data for the tenant (AC-1).
 * Returns null fields for new tenants without Party data.
 */
export const useBillingDataQuery = () => {
  return useQuery<SelfServiceBillingData, AxiosError>({
    queryKey: SELF_SERVICE_BILLING_KEYS.billingData,
    queryFn: () => selfServiceBillingApi.getBillingData(),
    staleTime: STALE_TIME,
  });
};

/**
 * Save billing data mutation (AC-3).
 * Invalidates billing data cache on success.
 *
 * Error handling:
 * - 429 -> rate limit (AC-6)
 * - 422 -> GUS unavailable
 * - other -> generic error
 */
export const useSaveBillingDataMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, SaveBillingDataPayload>({
    mutationFn: (payload) => selfServiceBillingApi.saveBillingData(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: SELF_SERVICE_BILLING_KEYS.billingData,
      });
    },
  });
};

// =============================================================================
// ORDERS
// =============================================================================

/**
 * Fetch paginated order list (AC-7).
 */
export const useOrdersQuery = (params: { limit: number; offset: number }) => {
  return useQuery<SelfServiceOrderListResponse, AxiosError>({
    queryKey: SELF_SERVICE_BILLING_KEYS.orders(params),
    queryFn: () => selfServiceBillingApi.getOrders(params),
    staleTime: STALE_TIME,
  });
};

/**
 * Fetch order detail (AC-9).
 * Enabled only when orderId is provided.
 */
export const useOrderDetailQuery = (orderId: string | null) => {
  return useQuery<SelfServiceOrderDetail, AxiosError>({
    queryKey: SELF_SERVICE_BILLING_KEYS.orderDetail(orderId ?? '__disabled__'),
    queryFn: () => selfServiceBillingApi.getOrderDetail(orderId!),
    staleTime: STALE_TIME,
    enabled: !!orderId,
  });
};
